import { faUpload } from "@fortawesome/pro-duotone-svg-icons";
import { Button, FileInput, Grid, Group, Text } from "@mantine/core";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../../custom-components/icons";
import Modal from "../../../custom-components/model";
import { TableData } from "../../../custom-components/table";
import t from "../../../stores/language";
import theme from "../../../stores/theme";
import { useStyles } from "../configure-input";
import { buildAssetPreview, setAssetPreview } from "./insurance-data";
import { useStore } from "../../../stores/store";
import { downloadExcelTemplate, readUploadRows } from "./insurance-sheet";

const assetHeaders = ["Asset ID", "Asset Name", "Code Type", "Replacement Cost ($)"];

const downloadAssetTemplate = () => {
  downloadExcelTemplate("Insurance Assets Template.xlsx", "Assets", assetHeaders);
};

export const InsuranceUploadModal = ({ opened, onClose, onUploaded }: { opened: boolean; onClose: () => void; onUploaded?: (rows: TableData[]) => void }) => {
  const { classes } = useStyles();
  const { store } = useStore();
  const navigateTo = useNavigate();
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadMessage, setUploadMessage] = useState<{ isError: boolean; message: string }>({ isError: false, message: "" });

  const resetUpload = () => {
    setUploadFile(null);
    setUploadMessage({ isError: false, message: "" });
    onClose();
  };

  const uploadAssets = () => {
    if (uploadFile == null) {
      setUploadMessage({ isError: true, message: t("Please Select File") });
      return;
    }
    readUploadRows(uploadFile)
      .then(async (sheetRows) => {
        const codeTypes = await store.GetInsuranceCodeTypes();
        const preview = buildAssetPreview(sheetRows, codeTypes);
        if (preview.error !== "") {
          setUploadMessage({ isError: true, message: t(preview.error) });
          return;
        }
        setAssetPreview(preview.rows);
        setUploadFile(null);
        setUploadMessage({ isError: false, message: "" });
        onClose();
        if (onUploaded) {
          onUploaded(preview.rows);
          return;
        }
        navigateTo("/insurance/preview");
      })
      .catch(() => {
        setUploadMessage({ isError: true, message: t("Unable to read the file. Upload an Excel sheet") });
      });
  };

  return (
    <Modal
      opened={opened}
      header={t("Upload Insurance Data")}
      footer={
        <Group className={classes.modalFooter}>
          <Button variant="default" onClick={resetUpload}>
            {t("Cancel")}
          </Button>
          <Button onClick={uploadAssets}>{t("Upload")}</Button>
        </Group>
      }
      withCloseButton={true}
      onClose={resetUpload}
      size="35%"
    >
      <Grid pb={15} pl={15} pr={15} columns={12} m={0}>
        <Grid.Col span={12}>
          <Button variant="light" color="blue" onClick={downloadAssetTemplate}>
            {t("Download Template")}
          </Button>
        </Grid.Col>
        <Grid.Col span={12}>
          <FileInput
            label={t("Insurance Data File")}
            value={uploadFile}
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(file) => {
              setUploadFile(file);
              setUploadMessage({ isError: false, message: "" });
            }}
            clearable
            icon={<Icon icon={faUpload} />}
          />
        </Grid.Col>
        {uploadMessage.message !== "" ? (
          <Grid.Col span={12}>
            <Text size={theme.fontSizes?.sm} color={uploadMessage.isError ? "red" : "green"}>
              {uploadMessage.message}
            </Text>
          </Grid.Col>
        ) : null}
      </Grid>
    </Modal>
  );
};

const InsuranceViewButton = () => {
  const navigateTo = useNavigate();

  return <Button onClick={() => navigateTo("/insurance/preview")}>{t("View")}</Button>;
};

export default InsuranceViewButton;
